<template>
  <div>
    <q-tabs
      v-model="selectedTab"
      inverted
      class="q-mb-md"
    >
      <q-tab
        slot="title"
        name="active"
        label="Active"
      />
      <q-tab
        slot="title"
        name="inactive"
        label="Incomplete"
      />
    </q-tabs>
    <div class="row full-row-exactly">
      <div class="col-12 flex-1">
        <div
          class="gutter-sm"
          :class="$q.screen.lt.md ? 'column' : 'row'"
        >
          <div
            v-if="$q.screen.lt.md || showFiltersDesktop"
            :class="$q.screen.lt.md && !showFiltersMobile ? 'col-auto' : 'col-md-3'"
          >
            <q-card :class="$q.screen.gt.sm ? 'full-height' : ''">
              <q-card-title>
                <div class="row">
                  <div class="col">
                    Filters
                  </div>
                  <div
                    v-if="$q.screen.lt.md"
                    class="col-auto q-mr-auto"
                  >
                    <q-btn
                      color="secondary"
                      flat
                      round
                      :icon="`keyboard_arrow_${showFiltersMobile ? 'up' :'down'}`"
                      @click="toggleFiltersMobile"
                    />
                  </div>
                </div>
              </q-card-title>
              <q-card-main
                v-if="$q.screen.gt.sm || showFiltersMobile"
                style="height: calc(100% - 64px)"
              >
                <component
                  :is="$q.screen.gt.sm ? 'q-scroll-area' : 'div'"
                  :class="{'full-height': $q.screen.gt.sm}"
                >
                  <bj-filters
                    ref="filtersModal"
                    :key="filtersResetReRenderKey"
                    v-model="filters"
                    :fields="filterFields"
                    @reset="filtersResetReRenderKey++"
                    @input="initFilters"
                  />
                </component>
              </q-card-main>
            </q-card>
          </div>
          <div class="col">
            <bj-data-table
              ref="dataTable"
              :with-add-button="false"
              :with-actions-column="selectedTab === 'inactive'"
              :with-cell-delete-button="false"
              :with-cell-edit-button="false"
              :filter-button="true"
              class="full-height"
              :query-params="queryParams"
              :columns="columns"
              many-name="Businesses"
              single-name="business"
              @toggle-filters="toggleFiltersDesktop"
              @fetch="fetchData"
            >
              <template
                slot="cell-name"
                slot-scope="{row}"
              >
                <router-link :to="{ name: 'app.businesses.profile', params: { id: row.id } }">
                  {{ row.name }} ({{ row.account_owner ? row.account_owner.full_name : '' }})
                </router-link>
              </template>
              <template
                slot="cell-units_count"
                slot-scope="{row}"
              >
                {{ row.active_units_count }}/{{ row.units_count }}
              </template>
              <template
                slot="cell-markets"
                slot-scope="{row}"
              >
                {{ row.markets.map(market => market.name).join(', ') }}
              </template>
              <template
                slot="cell-last_activity_at"
                slot-scope="{row}"
              >
                <SdDatetime
                  v-if="row.last_activity_at"
                  :value="row.last_activity_at"
                />
              </template>
              <template
                v-if="row.subscription"
                slot="cell-current_period_start_at"
                slot-scope="{row}"
              >
                <SdDatetime :value="row.subscription.current_period_start_at" />
              </template>
              <template
                slot="cell-created_at"
                slot-scope="{row}"
              >
                <SdDatetime :value="row.created_at" />
              </template>
              <template
                slot="cell-business_name"
                slot-scope="{row}"
              >
                <span v-if="row.business">row.business.name</span>
              </template>
              <template
                slot="cell-actions"
                slot-scope="{row}"
              >
                <q-tooltip v-if="row.cannot_be_deleted">
                  Cannot delete a business which has had a subscription
                </q-tooltip>
                <q-btn
                  :disabled="!!row.cannot_be_deleted"
                  class="q-ml-sm"
                  color="red"
                  size="sm"
                  rounded
                  label="Delete business"
                  icon="close"
                  outline
                  @click.prevent="deleteBusiness(row)"
                />
              </template>
              <template
                slot="cell-logo_url"
                slot-scope="{row}"
              >
                <img
                  :src="row.logo_url"
                  class="logo-url-image"
                >
              </template>
            </bj-data-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash.get';
import SdChangeBusinessCreditsModal from '../../../components/change-business-credits-modal';
import BjFilters from '../../../components/filters';

export default {
  components: { BjFilters, SdChangeBusinessCreditsModal },
  data() {
    return {
      selectedTab: 'active',
      filters: {
        markets: [],
        subscription_plans: [],
      },
      filterFields: ['markets', 'subscription_plans'],
      filtersResetReRenderKey: 1,
      showFiltersMobile: false,
      showFiltersDesktop: false,
      queryParams: {
        scopes: {},
      },
    };
  },
  computed: {
    columns() {
      if (this.selectedTab === 'inactive') {
        return inactiveColumns;
      }

      return [...baseColumns];
    },
  },
  watch: {
    selectedTab: {
      immediate: true,
      handler(selectedTab) {
        this.$refs.filtersModal && this.$refs.filtersModal.reset();
        if (this.queryParams.order_by) {
          delete this.queryParams.order_by;
        }
        this.queryParams.scopes = {
          [selectedTab]: true,
        };
        if (this.$refs.dataTable) {
          this.$refs.dataTable.serverData = [];
          this.$refs.dataTable.refresh();
        }
      },
    },
  },
  created() {
    [this.queryParams, this.filters] = this.applySortingAndFiltering(this.queryParams, this.filters);
  },
  methods: {
    get,
    fetchData(tableState) {
      const { queryParams } = tableState;
      if (this.queryParams !== null && (queryParams.page != this.queryParams.page
          || queryParams.limit != this.queryParams.limit)) {
        this.queryParams.page = queryParams.page;
        this.queryParams.limit = queryParams.limit;
      }
      if (this.selectedTab === 'inactive') {
        this.fetchInactiveUsers(tableState);
      } else {
        delete tableState.queryParams.order_by;
        this.fetchBusinesses(tableState);
      }
    },
    async fetchBusinesses(tableState) {
      this.api().request('GET', 'admin/businesses', {}, tableState.queryParams).then((response) => {
        this.setTableData(tableState, response);
      }).catch(() => {});
    },
    fetchInactiveUsers(tableState) {
      tableState.queryParams.scopes = { incompleteAccountOwners: true };
      if (!tableState.queryParams.order_by) {
        tableState.queryParams.order_by = 'created_at,desc';
      }
      this.api().request('GET', 'admin/users', {}, tableState.queryParams)
        .then((response) => {
          response.data = response.data.map((row) => ({
            cannot_be_deleted: get(row, 'business.subscription'),
            ...row,
          }));
          this.setTableData(tableState, response);
        })
        .catch(() => {});
    },
    setTableData({ queryParams, pagination }, response) {
      this.$refs.dataTable.serverPagination = pagination;
      this.$refs.dataTable.serverPagination.rowsNumber = response.total;
      this.$refs.dataTable.serverData = response.data;
      this.$refs.dataTable.loading = false;
      const queryParamsWithoutScopes = { ...queryParams };
      delete queryParamsWithoutScopes.scopes;
      this.addParamsToLocation(queryParamsWithoutScopes);
    },
    toggleFiltersMobile() {
      this.showFiltersMobile = !this.showFiltersMobile;
    },
    toggleFiltersDesktop() {
      this.showFiltersDesktop = !this.showFiltersDesktop;
    },
    deleteBusiness(user) {
      this.uiConfirm('Are you sure? Deleting a business is irreversable').then(() => {
        this.api().request('DELETE', `admin/users/${user.id}`)
          .then(() => {
            this.uiNotify('Business deleted successfully');
            this.$refs.dataTable.refresh();
          }).catch((response) => {
            this.uiNotify(response.message || 'Error occurred', 'negative');
          });
      });
    },
    initFilters() {
      Object.keys(this.filters).forEach((key) => {
        const filter = this.clone(this.filters[key]);

        if (filter && filter.length) {
          if ([].includes(key)) {
            this.queryParams.scopes[key] = filter;
          } else {
            this.queryParams[key] = filter;
          }
        } else if ([].includes(key)) {
          delete this.queryParams.scopes[key];
        } else {
          delete this.queryParams[key];
        }
      });
      setTimeout(() => {
        this.$refs.dataTable.refresh();
      }, 100);
    },
  },
};
const baseColumns = [
  {
    name: 'id',
    field: 'id',
    label: 'ID',
    sortable: true,
  },
  {
    name: 'name',
    field: 'name',
    label: 'Business name',
    sortable: true,
  },
  {
    name: 'units_count',
    field: 'units_count',
    label: 'Units (active/total)',
    sortable: false,
  },
  {
    name: 'markets',
    field: 'markets',
    label: 'Markets',
    sortable: false,
  },
  {
    name: 'last_activity_at',
    field: 'last_activity_at',
    label: 'Last activity',
    sortable: false,
  },
  {
    name: 'current_period_start_at',
    field: 'current_period_start_at',
    label: 'Subscription start date',
    sortable: false,
  },
  {
    name: 'website_url',
    field: 'website_url',
    label: 'Website url',
    sortable: false,
  },
  {
    name: 'logo_url',
    field: 'logo_url',
    label: 'Logo',
    sortable: false,
  },
];

const inactiveColumns = [
  {
    name: 'id',
    field: 'id',
    label: 'ID',
    sortable: true,
  },
  {
    name: 'email',
    field: 'email',
    label: 'Email',
    sortable: true,
  },
  {
    name: 'verified',
    field: 'verified',
    label: 'Verified Email',
    sortable: true,
  },
  {
    name: 'created_at',
    field: 'created_at',
    label: 'Signed Up At',
    sortable: true,
  },
  {
    name: 'phone',
    field: 'formatted_phone_number',
    label: 'Phone',
  },
  {
    name: 'inactive_name',
    field: 'full_name',
    label: 'Name',
  },
];
</script>
<style lang="scss" scoped>
  .logo-url-image{
    width: 100px;
  }
</style>
